import { colors, HStack, Text, VStack } from "@biblioteksentralen/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "../../../../utils/hooks/useTranslation";
import { CalendarDoorElement, GuessState, PublicationTag } from "../types";
import { CoverImageGrid } from "./CoverImageGrid";
import { GuessStateMap } from "./GuessAuthorCoverImage";
import { TitleOrAuthorTag } from "./TitleOrAuthorTag";

const RESET_TIMEOUT_MS = 1000;
const FINISHED_TIMEOUT_MS = 1000;

// We use static random orders to avoid issues with SSR vs client side
const randomOrders = [
  [6, 0, 3, 5, 2, 4, 1, 7],
  [3, 1, 5, 4, 2, 7, 0, 6],
  [3, 6, 5, 1, 4, 0, 7, 2],
  [7, 2, 4, 6, 1, 3, 0, 5],
  [3, 0, 5, 1, 2, 4, 6, 7],
  [0, 3, 2, 1, 7, 5, 4, 6],
  [1, 3, 0, 2, 5, 7, 4, 6],
  [7, 4, 3, 2, 1, 5, 0, 6],
  [7, 4, 0, 3, 6, 5, 1, 2],
  [4, 2, 5, 1, 0, 7, 6, 3],
];

const shuffleBy = <T,>(array: T[], orders: number[]): T[] => {
  if (array.length !== orders.length) {
    console.error("Array and orders need to be of the same length when shuffling");
    return array;
  }

  return orders.map((index) => array[index]!);
};

export const setupGuessStates = (numberOfPublications: number, state: GuessState): GuessStateMap[] =>
  Array(numberOfPublications)
    .fill(undefined)
    .map(() => ({ title: state, author: state }));

type UpdateGuessStates = (publicationIndex: number, type: PublicationTag["type"], updatedState: GuessState) => void;

const useResetIncorrect = (updateGuessStates: UpdateGuessStates) => {
  const [resettingIncorrect, setResettingIncorrect] = useState<
    { publicationIndex: number; type: PublicationTag["type"] }[]
  >([]);

  useEffect(() => {
    if (!resettingIncorrect.length) return;

    const timeoutId = setTimeout(() => {
      resettingIncorrect.forEach(({ publicationIndex, type }) => updateGuessStates(publicationIndex, type, undefined));
      setResettingIncorrect([]);
    }, RESET_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [resettingIncorrect, updateGuessStates]);

  return setResettingIncorrect;
};

export const GuessAuthor: CalendarDoorElement<"adventCalendarDoorGuessAuthor"> = ({
  door,
  setAnswered,
  setAttempts,
  date,
}) => {
  const { t } = useTranslation();
  const [NumberOfIncorrectGuesses, setNumberOfIncorrectGuesses] = useState(0);
  const numberOfPublications = door.publications?.length ?? 0;

  const [guessStates, setGuessStates] = useState<GuessStateMap[]>(setupGuessStates(numberOfPublications, undefined));
  const hasGuessedAll = guessStates.every(({ title }) => title === "correct");

  useEffect(() => {
    if (!hasGuessedAll) return;

    const onFinished = () => {
      setAnswered("correct");
      setAttempts(NumberOfIncorrectGuesses + 1);
    };

    const timeoutId = setTimeout(onFinished, FINISHED_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [hasGuessedAll, NumberOfIncorrectGuesses, setAnswered, setAttempts]);

  const tags: PublicationTag[] = shuffleBy(
    door.publications
      ?.filter(({ title, author }) => !!title && !!author)
      .flatMap(({ title, author }, publicationIndex) => [
        { value: title!, type: "title", publicationIndex },
        { value: author?.name!, type: "author", publicationIndex },
      ]) ?? [],
    randomOrders[(date - 1) % randomOrders.length]!
  );

  const updateGuessStates: UpdateGuessStates = useCallback(
    (publicationIndex, type, updatedState) =>
      setGuessStates((currentStates) =>
        currentStates.map((stateMap, index) =>
          index === publicationIndex ? { ...stateMap, [type]: updatedState } : stateMap
        )
      ),
    []
  );

  const setResettingIncorrect = useResetIncorrect(updateGuessStates);

  const handleClickTag = ({ publicationIndex, type }: PublicationTag) => {
    const currentIndex = guessStates.findIndex(({ title, author }) => [title, author].includes("current"));
    const currentState = guessStates[currentIndex];

    // Starting new guess
    if (!currentState) {
      updateGuessStates(publicationIndex, type, "current");
      return;
    }

    // Canceling current guess
    if (currentState && currentIndex === publicationIndex && currentState[type] === "current") {
      updateGuessStates(publicationIndex, type, undefined);
      return;
    }

    // Guessed correctly
    if (!!currentState && currentIndex === publicationIndex) {
      updateGuessStates(publicationIndex, "title", "correct");
      updateGuessStates(publicationIndex, "author", "correct");
      return;
    }

    // Guessed incorrectly
    if (currentState) {
      setNumberOfIncorrectGuesses((current) => current + 1);

      const currentIncorrectType = currentState.title === "current" ? "title" : "author";

      updateGuessStates(publicationIndex, type, "incorrect");
      updateGuessStates(currentIndex, currentIncorrectType, "incorrect");

      setResettingIncorrect((current) => [
        ...current,
        { publicationIndex, type },
        { publicationIndex: currentIndex, type: currentIncorrectType },
      ]);
      return;
    }

    console.error("Something went wrong handling tag click", type, publicationIndex);
  };

  return (
    <VStack gap="1rem" alignItems="flex-start">
      <CoverImageGrid publications={door.publications} guessStates={guessStates} />
      <VStack gap="0" alignItems="flex-start">
        <Text fontSize={{ base: "lg", md: "xl" }} fontWeight={600}>
          {t("Hvilken forfatter og tittel hører sammen?")}
        </Text>
        <Text fontSize="md" marginTop="0!important">
          {t("Trykk på boksene under for å markere og avgi svaret ditt")}
        </Text>
      </VStack>
      {!!tags?.length && (
        <HStack flexWrap="wrap" gap="0.5rem" alignItems="center" justifyContent="flex-start">
          {tags.map((tag) => (
            <TitleOrAuthorTag
              key={`${tag.publicationIndex}-${tag.type}`}
              tag={tag}
              onClick={handleClickTag}
              guessState={guessStates?.[tag.publicationIndex]?.[tag.type]}
            />
          ))}
        </HStack>
      )}
      <Text color={colors.accentGreen} fontWeight={600} fontSize="md">
        {t("Antall forsøk: {antall}", { antall: NumberOfIncorrectGuesses })}
      </Text>
    </VStack>
  );
};
